/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * The ONE reusable PDF template. It produces a formatted document a rep can send
 * to a distributor brand manager — used both for the UNSCORED program (before
 * sending) and the FINAL SCORED report (after month-end). Same template; the
 * scored sections only appear when scores are present.
 *
 * jsPDF is a project dependency; it's imported lazily so it stays out of the
 * main bundle. This renders text + light vector rules only (no user HTML), so
 * there's no injection surface — values go through jsPDF's text API.
 */

import { describePayout, attainmentPct, num } from "./payout.js";
import { METRIC_LABEL, STATUS_LABEL } from "./constants.js";

const BRAND_DEFAULT = [27, 42, 74];   // #1B2A4A navy — app default major color
// App default minor color — the app orange (backlog 145/166; was #E85D26).
// A PDF page is always white, and both uses here are contrast-critical: a
// setTextColor() line and a 1.5pt rule. So this is the LIGHT-theme TEXT value
// #8F5408 (4.57:1), not the #F2A33C fill, which measures 2.08:1 on white and is
// why styles.css needs a separate --o-fill-border in light theme.
// Only the DEFAULT changes — a tenant's own secondaryColor still wins below.
const ACCENT_DEFAULT = [143, 84, 8];  // #8F5408
const MUTED = [110, 120, 135];
const RULE = [210, 215, 224];

// "#rrggbb" -> [r,g,b]; anything invalid falls back.
function hexToRgb(hex, fallback) {
  if (typeof hex === "string" && /^#[0-9a-fA-F]{6}$/.test(hex.trim())) {
    const h = hex.trim();
    return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
  }
  return fallback;
}

// Pick black/white text for legibility on a colored fill.
function readableOn([r, g, b]) {
  return (r * 0.299 + g * 0.587 + b * 0.114) > 150 ? [20, 24, 32] : [255, 255, 255];
}

// Load an image URL as a PNG data URL (+ natural size) for jsPDF. We FETCH the
// bytes and load them through a data: URL (not the URL straight into <img>):
// that rasterizes EVERY format — SVG, PNG, JPEG, WebP — and never taints the
// canvas, so the PNG export always succeeds. (Loading the URL directly into
// <img crossOrigin> failed for the same-origin SVG logo; the data-URL path is
// verified to work.) Returns null on any failure — the logo is optional.
async function loadImageDataUrl(url) {
  try {
    if (!url || typeof document === "undefined" || typeof fetch === "undefined") return null;
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const srcUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => reject(new Error("read error"));
      fr.readAsDataURL(blob);
    });
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error("image load error"));
      im.src = srcUrl;
    });
    if (img.decode) { try { await img.decode(); } catch { /* best effort */ } }
    let w = img.naturalWidth || img.width || 0;
    let h = img.naturalHeight || img.height || 0;
    if (!w || !h) { w = 300; h = 100; } // SVG with no intrinsic size
    // Rasterize at >1x so the logo stays crisp in the PDF.
    const scale = Math.min(4, Math.max(2, 700 / w));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL("image/png"), width: w, height: h };
  } catch (e) {
    console.warn("[incentivePdf] logo load failed", e);
    return null;
  }
}

function money(n) {
  return `$${num(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/**
 * "Accounts: A, B +3 more   ·   SKUs: all" for one goal (item 233). Returns null
 * when the goal is scoped to everything, so an unscoped program doesn't gain a
 * line of noise on every goal.
 */
function scopeLine(goal) {
  const label = (list, max = 6) => {
    const arr = (Array.isArray(list) ? list : []).map((x) => x?.name || x?.id).filter(Boolean);
    if (arr.length === 0) return null;
    return arr.length <= max ? arr.join(", ") : `${arr.slice(0, max).join(", ")} +${arr.length - max} more`;
  };
  const accounts = label(goal?.target_accounts);
  const products = label(goal?.target_products);
  if (!accounts && !products) return null;
  return `Measured against — Accounts: ${accounts || "all"}     SKUs: ${products || "all"}`;
}

function fmtDate(d) {
  if (!d) return "—";
  // d is an ISO yyyy-mm-dd; render without timezone drift.
  const [y, m, day] = String(d).slice(0, 10).split("-");
  if (!y || !m || !day) return String(d);
  const dt = new Date(Number(y), Number(m) - 1, Number(day));
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Build + download the program PDF, branded with the company's logo + colors.
 * @param {{program:object, goals:object[], scores:object[], accountName?:string,
 *          logoUrl?:string, primaryColor?:string, secondaryColor?:string}} data
 * @param {{ scored?: boolean }} [opts]  force scored layout; otherwise inferred
 *   from the presence of scores.
 */
export async function exportProgramPdf(
  { program, goals = [], scores = [], accountName = "", logoUrl = "", primaryColor = "", secondaryColor = "" },
  opts = {},
) {
  // Defensive: depending on the bundle, jspdf exposes the constructor as a named
  // export OR as the module default. Accept either.
  const mod = await import("jspdf");
  const JsPDF = mod.jsPDF || mod.default || (mod.default && mod.default.jsPDF);
  if (typeof JsPDF !== "function") throw new Error("PDF engine failed to load.");
  const doc = new JsPDF({ unit: "pt", format: "letter" });

  // Company brand colors (fall back to the app palette).
  const brand = hexToRgb(primaryColor, BRAND_DEFAULT);
  const accent = hexToRgb(secondaryColor, ACCENT_DEFAULT);
  const logo = await loadImageDataUrl(logoUrl);

  const scoreByGoal = new Map(scores.map((s) => [s.goal_id, s]));
  const scored = opts.scored ?? scores.length > 0;

  const margin = 48;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - margin * 2;
  let y = margin;

  const ensureSpace = (need) => {
    if (y + need > pageH - margin) { doc.addPage(); y = margin; }
  };

  // ── Brand bar (major color) across the very top ─────────────────────────────
  doc.setFillColor(...brand);
  doc.rect(0, 0, pageW, 8, "F");

  // ── Logo, top-right (optional) ──────────────────────────────────────────────
  let logoBottom = y;
  if (logo) {
    const maxH = 46, maxW = 150;
    let h = maxH, w = h * (logo.width / logo.height);
    if (w > maxW) { w = maxW; h = w * (logo.height / logo.width); }
    doc.addImage(logo.dataUrl, "PNG", pageW - margin - w, y, w, h);
    logoBottom = y + h;
  }

  // ── Header band (left column) ───────────────────────────────────────────────
  if (accountName) {
    doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(...accent);
    doc.text(accountName.toUpperCase(), margin, y);
    y += 16;
  }
  doc.setFont("helvetica", "bold").setFontSize(20).setTextColor(...brand);
  doc.text(scored ? "Incentive Program — Final Report" : "Distributor Incentive Program", margin, y);
  y += 26;

  doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(40, 48, 64);
  const nameLines = doc.splitTextToSize(program.name || "Untitled program", contentW);
  doc.text(nameLines, margin, y);
  y += nameLines.length * 17 + 6;

  // Keep content clear of a tall logo.
  if (logoBottom + 8 > y) y = logoBottom + 8;

  // ── Meta grid ──────────────────────────────────────────────────────────────
  const states = Array.isArray(program.target_states) ? program.target_states : [];
  const meta = [
    ["State(s)", states.length ? states.join(", ") : "—"],
    ["Distributor", program.distributor_name || "—"],
    ["Brand manager", program.brand_manager_name || "—"],
    ["Program window", `${fmtDate(program.start_date)}  –  ${fmtDate(program.end_date)}`],
    ["Status", STATUS_LABEL[program.status] || program.status || "—"],
  ];
  doc.setFontSize(10);
  const colW = contentW / 2;
  meta.forEach((row, i) => {
    const col = i % 2;
    const rowY = y + Math.floor(i / 2) * 30;
    const x = margin + col * colW;
    doc.setFont("helvetica", "normal").setTextColor(...MUTED);
    doc.text(row[0].toUpperCase(), x, rowY);
    doc.setFont("helvetica", "bold").setTextColor(40, 48, 64);
    doc.text(doc.splitTextToSize(String(row[1]), colW - 12), x, rowY + 13);
  });
  y += Math.ceil(meta.length / 2) * 30 + 8;

  // accent rule (minor color)
  doc.setDrawColor(...accent).setLineWidth(1.5).line(margin, y, pageW - margin, y);
  y += 22;

  // ── Goals ──────────────────────────────────────────────────────────────────
  doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(...brand);
  doc.text("Goals & Payouts", margin, y);
  y += 18;

  if (goals.length === 0) {
    doc.setFont("helvetica", "italic").setFontSize(10).setTextColor(...MUTED);
    doc.text("No goals defined.", margin, y);
    y += 16;
  }

  let totalEarned = 0;
  goals.forEach((g, idx) => {
    ensureSpace(96); // title + detail + optional scope line + optional score line
    const s = scoreByGoal.get(g.id);
    if (s) totalEarned += num(s.payout_earned);

    // Goal title
    doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(35, 42, 58);
    const title = `${idx + 1}. ${g.description || METRIC_LABEL[g.metric_type] || "Goal"}`;
    const titleLines = doc.splitTextToSize(title, contentW);
    doc.text(titleLines, margin, y);
    y += titleLines.length * 14 + 2;

    // Detail line: (custom free text) OR metric · target · payout structure
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(70, 78, 92);
    const isCustom = g.metric_type === "custom";
    const unit = g.unit_label ? ` ${g.unit_label}` : "";
    const detail = isCustom
      ? `${g.custom_text || "Custom goal"}\nPayout: ${describePayout(g)}`
      : `Metric: ${METRIC_LABEL[g.metric_type] || g.metric_type}     `
        + `Target: ${num(g.target_value).toLocaleString()}${unit}     `
        + `Payout: ${describePayout(g)}`;
    const detailLines = doc.splitTextToSize(detail, contentW);
    doc.text(detailLines, margin, y);
    y += detailLines.length * 13 + 2;

    // Scope line (item 233): the accounts and SKUs the goal is measured against.
    // This is the half of the agreement a brand manager argues about at
    // month-end, so it belongs on the document they receive, not just on screen.
    const scope = scopeLine(g);
    if (scope) {
      doc.setFont("helvetica", "italic").setFontSize(9).setTextColor(...MUTED);
      const scopeLines = doc.splitTextToSize(scope, contentW);
      doc.text(scopeLines, margin, y);
      y += scopeLines.length * 12 + 2;
      doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(70, 78, 92);
    }

    // Scored line
    if (scored) {
      const actual = s ? num(s.actual_value) : null;
      const earned = s ? num(s.payout_earned) : 0;
      doc.setFont("helvetica", "bold").setFontSize(10);
      if (s) {
        if (isCustom) {
          doc.setTextColor(40, 48, 64);
          const line = `Result: ${actual.toLocaleString()}      Payout earned: ${money(earned)}`;
          doc.text(doc.splitTextToSize(line, contentW), margin, y);
        } else {
          const pct = attainmentPct(actual, g.target_value);
          doc.setTextColor(pct >= 100 ? 22 : 150, pct >= 100 ? 130 : 90, pct >= 100 ? 70 : 30);
          const line = `Achieved: ${actual.toLocaleString()}${unit}  (${pct.toFixed(0)}% of target)`
            + `      Payout earned: ${money(earned)}`;
          doc.text(doc.splitTextToSize(line, contentW), margin, y);
        }
      } else {
        doc.setTextColor(...MUTED);
        doc.text("Not scored.", margin, y);
      }
      y += 15;
    }

    // separator
    ensureSpace(14);
    doc.setDrawColor(...RULE).setLineWidth(0.5).line(margin, y, pageW - margin, y);
    y += 14;
  });

  // ── Totals (scored only) — a branded bar in the major color ─────────────────
  if (scored && goals.length > 0) {
    ensureSpace(34);
    doc.setFillColor(...brand);
    doc.rect(margin, y - 4, contentW, 26, "F");
    const tc = readableOn(brand);
    doc.setFont("helvetica", "bold").setFontSize(12).setTextColor(...tc);
    doc.text("Total payout earned", margin + 12, y + 13);
    doc.text(money(totalEarned), pageW - margin - 12, y + 13, { align: "right" });
    y += 34;
  }

  // ── Footer on every page ───────────────────────────────────────────────────
  const generated = new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...MUTED);
    doc.text(`Generated ${generated}${accountName ? ` · ${accountName}` : ""}`, margin, pageH - 24);
    doc.text(`Page ${p} of ${pages}`, pageW - margin, pageH - 24, { align: "right" });
  }

  // Sanitize → trim stray dashes → fall back if the name was all punctuation.
  const safeName = String(program.name || "")
    .replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase().slice(0, 60)
    || "incentive-program";
  doc.save(`${safeName}${scored ? "-final-report" : ""}.pdf`);
}
